import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatSort } from '@angular/material/sort'
import { EraajotYksiKerrallaanSuorittajaData } from '@lemon/core-firebase/model/eraajot'
import { DateService } from '@lemon/core/service/date.service'
import {
  EraajotStatusHaeOrpo,
  EraajotStatusHaeReq,
  EraajotStatusHaeResp,
  EraajotStatusHaeRespRivi,
  liikennevaloSevereetti,
} from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/eraajot-status'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { EnvironmentType } from 'app/app.environment'
import { environment } from 'environments/environment'
import { BehaviorSubject, filter, map, Observable, Subject, switchMap, takeUntil } from 'rxjs'
import { DebugService } from '../../_angular/service/debug.service'
import { Asiakas } from '../../_jaettu-lemonator/model/asiakas'
import {
  ItTyopoytaHaeLogitRequest,
  ItTyopoytaHaeLogitResponse,
  ItTyopoytaHaeTyojonotRequest,
  ItTyopoytaHaeTyojonotResponse,
  ItTyopoytaUudelleenkaynnistaTyojonoRequest,
  ItTyopoytaUudelleenkaynnistaTyojonoResponse,
} from '../../_jaettu-lemonator/model/it-tyopoyta'
import { LogEntry } from '../../_jaettu-lemonator/model/logging'

interface ViewableTyojono {
  data: Object
  tyojonoUri: string
  restartState: 'loading' | 'done'
}

@Component({
  templateUrl: './it-tyopoyta.component.html',
  styleUrls: ['./it-tyopoyta.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ItTyopoytaComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe: Subject<void> = new Subject<void>()

  private _matSort: MatSort | null = null
  @ViewChild(MatSort) set matSortRef(sort: MatSort | undefined) {
    if (sort && sort !== this._matSort) {
      this._matSort = sort
      sort.sortChange.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
        const nykyinen = this.eraajotStatus$.value
        if (!nykyinen) {
          return
        }
        this.eraajotStatus$.next({ ...nykyinen, tilat: this._lajitteleTilat(nykyinen.tilat) })
      })
    }
  }

  readonly lemonatorTyojonot = signal<ViewableTyojono[] | undefined>(undefined)
  lemonatorTyojonoAjuritObservable: Observable<{ data: EraajotYksiKerrallaanSuorittajaData; tyojonoUri: string }[]>
  readonly lemonaidTyojonot = signal<ViewableTyojono[] | undefined>(undefined)
  lemonaidTyojonoAjuritObservable: Observable<{ data: EraajotYksiKerrallaanSuorittajaData; tyojonoUri: string }[]>

  private _forceTyojonotFetchSubject: BehaviorSubject<'lemonaid' | 'lemonator'> = new BehaviorSubject(null)

  readonly lemonatorLoading = signal(false)
  readonly lemonaidLoading = signal(false)

  readonly lemonaidLogs = signal<LogEntry[]>([])
  readonly lemonatorLogs = signal<LogEntry[]>([])

  private readonly _changeDetectorRef = inject(ChangeDetectorRef)

  eraajotStatus$ = new BehaviorSubject<EraajotStatusHaeResp | null>(null)
  eraajotLatauksessa$ = new BehaviorSubject<boolean>(false)

  constructor(
    private _firebaseLemonator: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _ladataanService: LadataanService,
    private _asiakasService: AsiakasService,
    private _debugService: DebugService,
    private _snackbar: MatSnackBar,
    private _dateService: DateService,
  ) {}

  ngOnInit() {
    // Haetaan eräajojen status heti sivun avautuessa
    void this.paivitaEraajotStatus()

    const lemonatorTyojonotObservable = this._forceTyojonotFetchSubject.pipe(
      filter(project => project === 'lemonator'),
      switchMap(project => {
        this.lemonatorLoading.set(true)
        const reqData: ItTyopoytaHaeTyojonotRequest = {
          project: project,
        }
        return this._firebaseLemonator
          .functionsCall<ItTyopoytaHaeTyojonotRequest, ItTyopoytaHaeTyojonotResponse>('itTyopoytaHaeTyojonot', reqData)
          .then(resp => {
            if (!resp || resp.e) {
              console.error('Failed to get Lemonator työjonot')
              return []
            }
            return resp.tyojonot
          })
          .finally(() => {
            this.lemonatorLoading.set(false)
          })
      }),
    )

    lemonatorTyojonotObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(lemonatorTyojonot => {
      this.lemonatorTyojonot.set(lemonatorTyojonot)
    })

    const lemonaidTyojonotObservable = this._forceTyojonotFetchSubject.pipe(
      filter(project => project === 'lemonaid'),
      switchMap(project => {
        this.lemonaidLoading.set(true)
        const reqData: ItTyopoytaHaeTyojonotRequest = {
          project: project,
        }
        return this._firebaseLemonator
          .functionsCall<ItTyopoytaHaeTyojonotRequest, ItTyopoytaHaeTyojonotResponse>('itTyopoytaHaeTyojonot', reqData)
          .then(resp => {
            if (!resp || resp.e) {
              console.error('Failed to get Lemonaid työjonot')
              return []
            }
            return resp.tyojonot
          })
          .finally(() => {
            this.lemonaidLoading.set(false)
          })
      }),
    )

    lemonaidTyojonotObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(lemonaidTyojonot => {
      this.lemonaidTyojonot.set(lemonaidTyojonot)
    })

    this._forceTyojonotFetchSubject.next('lemonaid')
    setTimeout(() => {
      this._forceTyojonotFetchSubject.next('lemonator')
    }, 1500)

    this.lemonaidTyojonoAjuritObservable = this._firebaseLemonaid
      .firestoreCollection('tyojono_ajurit/')
      .listenSnapshots()
      .pipe(
        map(resp =>
          resp.map(doc => {
            return { data: doc.data() as EraajotYksiKerrallaanSuorittajaData, tyojonoUri: doc.ref.path }
          }),
        ),
      )
    this.lemonatorTyojonoAjuritObservable = this._firebaseLemonator
      .firestoreCollection('tyojono_ajurit/')
      .listenSnapshots()
      .pipe(
        map(resp =>
          resp.map(doc => {
            return { data: doc.data() as EraajotYksiKerrallaanSuorittajaData, tyojonoUri: doc.ref.path }
          }),
        ),
      )
  }

  openLink(uri: string, project: 'lemonaid' | 'lemonator', event: Event) {
    event.preventDefault()
    event.stopPropagation()

    if (project === 'lemonator') {
      window.open(this._debugService.createFirestoreLink(uri))
      return
    }

    if (project === 'lemonaid') {
      window.open(this._debugService.createFirestoreLinkLemonaid(uri))
      return
    }

    throw new Error('No handling for project ' + project)
  }

  async openCustomer(uri: string, event: Event) {
    event.preventDefault()
    event.stopPropagation()

    const split = uri.split('/')
    const asiakasAvainOrAsiakasId = split[1]

    const asiakasAvain = isNaN(Number(asiakasAvainOrAsiakasId)) ? asiakasAvainOrAsiakasId : null

    if (asiakasAvain) {
      window.open(this._annaAsiakkaanLemonatorLinkki(asiakasAvain))
      return
    }

    const asiakas = await this._firebaseLemonator
      .firestoreCollection<Asiakas>('asiakkaat/')
      .where('asiakasId', '==', Number(asiakasAvainOrAsiakasId))
      .get()
      .then(resp => resp?.[0])
    if (!asiakas) {
      this._snackbar.open('En löytänyt asiakasta tunnuksella ' + asiakasAvainOrAsiakasId, 'OK')
      return
    }
    window.open(this._annaAsiakkaanLemonatorLinkki(asiakas.avain))
    return
  }

  private _annaAsiakkaanLemonatorLinkki(asiakasAvain: string): string {
    if (environment.environment === EnvironmentType.PRODUCTION) {
      return `https://lemonator.lemontree.fi/asiakkaat/${asiakasAvain}/asiakastiedot`
    } else if (environment.environment === EnvironmentType.BETA) {
      return `https://beta.lemonator.lemontree.fi/asiakkaat/${asiakasAvain}/asiakastiedot`
    } else if (environment.environment === EnvironmentType.DEV) {
      return `https://dev.lemonator.lemontree.fi/asiakkaat/${asiakasAvain}/asiakastiedot`
    } else if (environment.environment === EnvironmentType.LOCAL_DEV) {
      return `https://local-dev.lemonator.lemontree.fi:4000/asiakkaat/${asiakasAvain}/asiakastiedot`
    }
    throw new Error('Unknown environment ' + environment.environment)
  }

  openCloudLoggingConsole(project: 'lemonaid' | 'lemonator', trace: string): string {
    if (project === 'lemonator') {
      window.open(this._debugService.createCloudLoggingLinkLemonator(trace))
      return
    }

    if (project === 'lemonaid') {
      window.open(this._debugService.createCloudLoggingLinkLemonaid(trace))
      return
    }

    throw new Error('No handling for project ' + project)
  }

  async fetchLogs(project: 'lemonator' | 'lemonaid') {
    this._ladataanService.aloitaLataaminen()

    const MINUTE_SPAN = 15
    const reqData: ItTyopoytaHaeLogitRequest = {
      project: project,
      start: this._dateService.lisaaMinuutteja(new Date(), -MINUTE_SPAN).toISOString(),
      end: new Date().toISOString(),
    }
    const resp = await this._firebaseLemonator.functionsCall<ItTyopoytaHaeLogitRequest, ItTyopoytaHaeLogitResponse>('itTyopoytaHaeLogit', reqData).finally(() => {
      this._ladataanService.lopetaLataaminen()
    })
    if (!resp || resp.e) {
      console.error('Failed to fetch ' + project + ' logs')
      return
    }

    if (!resp.entries?.length) {
      this._snackbar.open('En löytänyt asiaankuuluvia ' + project + 'in logeja viimeisen ' + MINUTE_SPAN + ' minuutin ajalta.', 'OK')
      return
    }

    if (project === 'lemonaid') {
      this.lemonaidLogs.set(resp.entries || [])
    }

    if (project === 'lemonator') {
      this.lemonatorLogs.set(resp.entries || [])
    }
  }

  async restartTyojono(project: 'lemonator' | 'lemonaid', tyojono: ViewableTyojono, event: Event) {
    event.preventDefault()
    event.stopPropagation()

    tyojono.restartState = 'loading'

    const reqData: ItTyopoytaUudelleenkaynnistaTyojonoRequest = {
      project: project,
      tyojonot: [{ uri: tyojono.tyojonoUri, uudelleenkaynnistystapa: tyojono.data.hasOwnProperty('uudelleenyrityksia') ? 'uudelleenyrityksia-reset' : 'simple-update' }],
    }
    const resp = await this._firebaseLemonator.functionsCall<ItTyopoytaUudelleenkaynnistaTyojonoRequest, ItTyopoytaUudelleenkaynnistaTyojonoResponse>('itTyopoytaUudelleenkaynnistaTyojonot', reqData)
    if (!resp || resp.e) {
      console.error('Failed to restart työjono' + tyojono.tyojonoUri)
      return
    }
    tyojono.restartState = 'done'
    this._changeDetectorRef.markForCheck()
  }

  async restartAllTyojonot(project: 'lemonator' | 'lemonaid') {
    const tyojonot = project === 'lemonaid' ? this.lemonaidTyojonot() : this.lemonatorTyojonot()

    if (!tyojonot?.length) {
      return
    }

    const tyojonoUriJaTapa: ItTyopoytaUudelleenkaynnistaTyojonoRequest['tyojonot'] = tyojonot.map(tj => {
      return { uri: tj.tyojonoUri, uudelleenkaynnistystapa: tj.data.hasOwnProperty('uudelleenyrityksia') ? 'uudelleenyrityksia-reset' : 'simple-update' }
    })

    for (const tyojono of tyojonot) {
      tyojono.restartState = 'loading'
    }

    const reqData: ItTyopoytaUudelleenkaynnistaTyojonoRequest = {
      project: project,
      tyojonot: tyojonoUriJaTapa,
    }
    const resp = await this._firebaseLemonator.functionsCall<ItTyopoytaUudelleenkaynnistaTyojonoRequest, ItTyopoytaUudelleenkaynnistaTyojonoResponse>('itTyopoytaUudelleenkaynnistaTyojonot', reqData)
    if (!resp || resp.e) {
      console.error('Failed to restart ' + project + ' työjonot')
      return
    }

    for (const tyojono of tyojonot) {
      tyojono.restartState = 'done'
    }
    this._changeDetectorRef.markForCheck()
  }

  async paivitaEraajotStatus() {
    this.eraajotLatauksessa$.next(true)
    try {
      const resp = await this._firebaseLemonator.functionsCall<EraajotStatusHaeReq, EraajotStatusHaeResp>('itTyopoytaHaeEraajotStatus', {})
      if (!resp) {
        this._snackbar.open('Eräajojen statuksen haku epäonnistui (ei vastausta)', 'OK')
        return
      }
      if (resp.e) {
        console.error('Eräajojen statuksen haku epäonnistui:', resp.e)
        this._snackbar.open('Eräajojen statuksen haku epäonnistui: ' + resp.e, 'OK')
      }
      this.eraajotStatus$.next({ ...resp, tilat: this._lajitteleTilat(resp.tilat) })
    } catch (err) {
      console.error('Eräajojen statuksen haku heitti:', err)
      this._snackbar.open('Eräajojen statuksen haku heitti virheen', 'OK')
    } finally {
      this.eraajotLatauksessa$.next(false)
    }
  }

  private _lajitteleTilat(tilat: EraajotStatusHaeRespRivi[]): EraajotStatusHaeRespRivi[] {
    const active = this._matSort?.active ?? 'valo'
    const direction = this._matSort?.direction ?? 'asc'
    if (!direction) {
      return [...tilat]
    }
    const mult = direction === 'desc' ? -1 : 1
    const kopio = [...tilat]
    if (active === 'valo') {
      kopio.sort((a, b) => mult * (liikennevaloSevereetti(a.valo) - liikennevaloSevereetti(b.valo)))
    } else if (active === 'projekti') {
      kopio.sort((a, b) => mult * a.projekti.localeCompare(b.projekti))
    } else if (active === 'selite') {
      kopio.sort((a, b) => mult * a.selite.localeCompare(b.selite))
    } else if (active === 'seuraavaOdotettuAjoIso' || active === 'lastSucceededIso') {
      kopio.sort((a, b) => {
        const va = a[active]
        const vb = b[active]
        // Null-arvot aina loppuun, suunnasta riippumatta
        if (!va && !vb) {
          return 0
        }
        if (!va) {
          return 1
        }
        if (!vb) {
          return -1
        }
        return mult * va.localeCompare(vb)
      })
    }
    return kopio
  }

  muotoileIso(iso: string | null): string {
    if (!iso) {
      return '—'
    }
    const date = this._dateService.parsiIso8601(iso)
    if (!date) {
      return iso
    }
    return this._dateService.muotoilePaivaJaAikaDate(date, 'fi')
  }

  trackEraajoRivi(_index: number, rivi: EraajotStatusHaeRespRivi): string {
    return rivi.projekti + ':' + rivi.topic
  }

  annaSchedulerLinkki(rivi: EraajotStatusHaeRespRivi): string {
    return rivi.projekti === 'lemonator' ? this._debugService.createCloudSchedulerEditLinkLemonator(rivi.selite) : this._debugService.createCloudSchedulerEditLinkLemonaid(rivi.selite)
  }

  annaStatusDokumenttiLinkki(rivi: EraajotStatusHaeRespRivi | EraajotStatusHaeOrpo): string {
    const uri = 'eraajot-status/' + rivi.topic
    return rivi.projekti === 'lemonator' ? this._debugService.createFirestoreLink(uri) : this._debugService.createFirestoreLinkLemonaid(uri)
  }

  trackOrpoRivi(_index: number, orpo: { projekti: string; topic: string }): string {
    return orpo.projekti + ':' + orpo.topic
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
